import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getClassRisks, getEnterpriseRisks, readableApiError } from '../../api/dashboard';
import { LEVEL_COLOR } from './_styles';
import './dashboard.css';

const LEVELS = ['极高', '高', '中', '低'];

export default function ClassRisksPage({ scope = 'class' }) {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [openLevels, setOpenLevels] = useState({ '极高': true, '高': true, '中': false, '低': false });

  const isEnt = scope === 'enterprise';
  const basePath = isEnt ? `/dashboard/enterprises/${id}` : `/dashboard/classes/${id}`;

  useEffect(() => {
    setData(null); setError(null);
    (isEnt ? getEnterpriseRisks(id) : getClassRisks(id))
      .then(setData)
      .catch((err) => setError(readableApiError(err)));
  }, [id, isEnt]);

  if (error) return <div className="aiq-dash dash-page"><div className="dash-card dash-card--error">{error}</div></div>;
  if (!data) return <div className="aiq-dash dash-page"><div className="dash-card">加载中...</div></div>;

  const grouped = LEVELS.map((lv) => ({
    level: lv,
    items: data.items.filter((x) => x.level === lv),
    count: data.summary.counts_by_level?.[lv] ?? 0,
  }));

  const totalRisks = data.items.length;

  return (
    <div className="aiq-dash dash-page">
      <div className="dash-head">
        <h1 className="dash-h1">风险预警</h1>
        <p className="dash-sub">引擎来自 RiskTemplate 表（DB 驱动）。规则与文案后台可改（M23 风险模板编辑）。</p>
      </div>

      <Link to={basePath} className="dash-back">← 返回总览</Link>

      {/* 团队整体态势 */}
      <div className="dash-card">
        <div className="dash-sec"><h2>团队整体风险态势</h2></div>
        {totalRisks === 0 ? (
          <div className="dash-mut">暂无风险（团队各维度均高于阈值）。</div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {LEVELS.map((lv) => (
                <div key={lv} className="dash-badge" style={{
                  padding: '8px 14px', borderRadius: 8,
                  background: LEVEL_COLOR[lv].bg, color: LEVEL_COLOR[lv].fg,
                }}>
                  {lv} {data.summary.counts_by_level?.[lv] ?? 0} 人次
                </div>
              ))}
            </div>
            {data.summary.top_risk_dims?.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div className="dash-mut" style={{ marginBottom: 6 }}>触达人数最多的维度：</div>
                {data.summary.top_risk_dims.slice(0, 5).map((d) => (
                  <div key={d.dim_code} style={{ fontSize: 13, marginBottom: 4 }}>
                    · 「{d.dim_name}」<b>{d.affected}/{d.total}</b> 人偏低
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* 分级列表 */}
      {grouped.map(({ level, items, count }) => (
        <div key={level} className="dash-card">
          <div className="dash-collapse-hd" onClick={() => setOpenLevels((s) => ({ ...s, [level]: !s[level] }))}>
            <h2>
              <span className="dash-badge" style={{ background: LEVEL_COLOR[level].bg, color: LEVEL_COLOR[level].fg }}>
                {level}
              </span>
              风险（{count} 人次）
            </h2>
            <span className="dash-caret">{openLevels[level] ? '▾' : '▸'}</span>
          </div>
          {openLevels[level] && (
            items.length === 0 ? (
              <div className="dash-mut" style={{ marginTop: 12 }}>无该等级风险条目。</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
                {items.map((it, idx) => (
                  <div key={idx} className="dash-risk">
                    <div className="dash-risk__hd">
                      <span className="dash-risk__who">
                        {it.student.display_name || '—'}
                        {it.student.position ? `（${it.student.position}）` : ''}
                      </span>
                      <span className="dash-risk__meta">{it.dim_name} {it.score} / 阈值 {it.threshold}</span>
                    </div>
                    <div className="dash-risk__bd">{it.text}</div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      ))}

      {data.summary.cache_hit && (
        <div className="dash-mut" style={{ fontSize: 11 }}>
          数据来自缓存（{data.summary.generated_at}）；班级状态变化时自动刷新。
        </div>
      )}
    </div>
  );
}
